// src/oura/ask/YesDialog.tsx
//
// Ask Oura (PREVIEW), use case 3: a named person's yes in front of the Page
// workshop's own "Get the copy to paste", when every change in the set is an
// answer the client approved in the Answer Plan. It shows each change before
// and after, whose approval it rests on, and who is saying yes now. Yes runs
// the workshop's own action (it copies; on a site Oura can write to, the
// workshop's own Publish confirm follows); Cancel runs nothing. Nothing here
// writes: the name is shown, not saved, in this preview.

import { useEffect, useRef, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import { FOCUS_RING, useEscapeLayer } from "../ui";
import { dayMonth } from "../visibility/format";
import type { AgreedSet } from "./agreed";
import { CardHead } from "./AskOura";
import { andList, n } from "./text";

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface YesChange {
    id: string;
    before: string;
    after: string;
}

export function PlanYesDialog({
    changes,
    agreed,
    name,
    pasteOn,
    onYes,
    onCancel,
}: {
    changes: YesChange[];
    agreed: AgreedSet;
    /** The signed-in person: the named yes. */
    name: string | null;
    /** The site a person pastes on ("Wix"), when Oura cannot write to it. */
    pasteOn: string | null;
    onYes: () => void;
    onCancel: () => void;
}) {
    useEscapeLayer(onCancel, true);
    const yes = useRef<HTMLButtonElement>(null);
    const panel = useRef<HTMLDivElement>(null);
    useEffect(() => {
        yes.current?.focus();
    }, []);
    // aria-modal alone does not contain focus: Tab would walk into the page
    // behind while the workshop awaits this answer. Wrap Tab inside the panel.
    const trapTab = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== "Tab") return;
        const items = Array.from(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };
    const k = changes.length;
    const days = Array.from(new Set(agreed.answers.map((x) => dayMonth(x.at))));
    const who = name ?? "you";
    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#111827]/30 p-4" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
            <div ref={panel} onKeyDown={trapTab} role="dialog" aria-modal="true" aria-label={`Say yes to ${n(k, "change")}`} className="max-h-[90vh] w-full max-w-[560px] overflow-y-auto rounded-2xl bg-white p-6 shadow-[0_24px_64px_-24px_rgba(17,24,39,0.35)] ring-1 ring-black/[0.08]" data-plan-yes>
                <CardHead label="Your yes · Preview, nothing is saved or sent" title={`Say yes to ${n(k, "change")} on the homepage`} />
                <p className="m-0 mt-2 text-sm leading-[22px] text-[#111827]">
                    {agreed.clientName} approved {k === 1 ? "this wording" : k === 2 ? "both" : `all ${k}`} in the {agreed.docTitle} on {andList(days)}. Your yes is the last one before the site changes.
                </p>
                <ol className="list-plain m-0 mt-4 space-y-3 p-0">
                    {changes.map((c, i) => (
                        <li key={c.id} className="rounded-xl bg-[#F9FAFB] p-3" data-plan-yes-change>
                            <p className="m-0 text-xs font-medium leading-4 text-[#6B7280]">Change {i + 1}</p>
                            <div className="mt-2 grid grid-cols-[44px_1fr] gap-x-3 gap-y-2">
                                <span className="pt-0.5 text-xs font-medium text-[#6B7280]">Now</span>
                                <span className="text-[13px] leading-[19px] text-[#6B7280]">{c.before}</span>
                                <span className="pt-0.5 text-xs font-medium text-blue-600">New</span>
                                <span className="border-l-2 border-blue-600 pl-2.5 text-[13px] font-medium leading-[19px] text-[#111827]">{c.after}</span>
                            </div>
                        </li>
                    ))}
                </ol>
                <p className="m-0 mt-4 text-[13px] leading-[19px] text-[#6B7280]" data-plan-yes-name>
                    Saying yes as <span className="font-medium text-[#111827]">{who}</span>.{" "}
                    {pasteOn ? `The site is on ${pasteOn}, so Oura copies the new wording for you to paste. Then Oura checks the page.` : "The workshop's own Publish confirm comes next."}
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className={cn("inline-flex h-9 items-center rounded-full bg-white px-4 text-[13px] font-medium text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50", FOCUS_RING)}
                    >
                        Cancel
                    </button>
                    <button
                        ref={yes}
                        type="button"
                        onClick={onYes}
                        className={cn("inline-flex h-9 items-center rounded-full bg-blue-600 px-4 text-[13px] font-medium text-white hover:bg-blue-700", FOCUS_RING)}
                        data-plan-yes-confirm
                    >
                        {pasteOn ? `Yes, copy ${k === 1 ? "it" : `the ${k} changes`}` : "Yes, continue"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
